import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { BackupRecipientsDialog } from "./BackupRecipientsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const when = (iso) =>
  new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const sentTo = (row) => row.email_sent_to || (typeof row.email_to === "string" && row.email_sent_at ? [row.email_to] : []);

/** Caption under the backup button: last nightly copy, recipients, and a "send now" action. */
export const BackupStatus = () => {
  const [last, setLast] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [h, r] = await Promise.all([
        client.get("/admin/backup/history", { params: { limit: 1 } }),
        client.get("/admin/backup/recipients"),
      ]);
      setLast(h.data.backups[0] || null);
      setRecipients(r.data);
    } catch { /* caption stays quiet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await client.post("/admin/backup/run-now");
      if (r.data.ok) toast.success(`Backup link emailed to ${r.data.to.join(", ") || "nobody (email failed)"}.`);
      else toast.error(`Backup failed: ${r.data.error}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run the backup.");
    } finally {
      setSending(false);
    }
  };

  const summary = !last
    ? "No nightly copy yet. Runs 1:00 AM Pacific."
    : last.status === "ready"
    ? `Last copy ${when(last.created_at)} · ${last.collections} collections · emailed to ${sentTo(last).length || "no"} recipient${sentTo(last).length === 1 ? "" : "s"}`
    : last.status === "failed"
    ? `Last copy ${when(last.created_at)} failed`
    : `Copy in progress since ${when(last.created_at)}`;

  const total = recipients ? 1 + recipients.extra.length : null;

  return (
    <div className="space-y-1 text-[11px] leading-snug text-olive" data-testid="backup-status">
      <p className={last?.status === "failed" ? "text-scarlet" : undefined} data-testid="backup-status-text">{summary}</p>
      <p className="flex flex-wrap items-center gap-x-3">
        <button type="button" onClick={() => setEditing(true)} data-testid="backup-recipients-open" className="inline-flex items-center gap-1 font-semibold text-ink underline-offset-2 hover:underline">
          <Users className="h-3 w-3" strokeWidth={2.2} />
          {total ? `${total} recipient${total === 1 ? "" : "s"}` : "Recipients"}
        </button>
        <button type="button" onClick={sendNow} disabled={sending} data-testid="backup-send-now" className="inline-flex items-center gap-1 font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-50">
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" strokeWidth={2.2} />}
          {sending ? "Sending…" : "Send nightly copy now"}
        </button>
      </p>
      <BackupRecipientsDialog open={editing} onOpenChange={setEditing} client={client} onSaved={(r) => { setRecipients(r); toast.success("Backup recipients saved."); }} />
    </div>
  );
};
